import * as turf from "@turf/turf"
import type { Feature, FeatureCollection, Point, Polygon } from "geojson"
import { memo, useCallback, useMemo, useState } from "react"

import {
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler } from "../typings"
import { Layer, Source } from "./memo"

const DIAMOND_DRAWER_SOURCE_ID = "diamond-drawer-source"

enum DiamondDrawerLayedId {
	CIRCLE = "diamond-drawer-circle-layer",
	LINE = "diamond-drawer-line-layer",
	FILL = "diamond-drawer-fill-layer",
}

interface DiamondDrawerProps {
	onComplete?: (diamond: Polygon) => void
}

export const DiamondDrawer = memo<DiamondDrawerProps>((props) => {
	const { onComplete } = props

	const [firstPoint, setFirstPoint] = useState<Feature<Point> | null>(null)
	const [secondPoint, setSecondPoint] = useState<Feature<Point> | null>(null)
	const [tempPoint, setTempPoint] = useState<Feature<Point> | null>(null)

	const reset = useCallback(() => {
		setFirstPoint(null)
		setSecondPoint(null)
		setTempPoint(null)
	}, [])

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint) {
				setFirstPoint(point)
			} else if (!secondPoint) {
				setSecondPoint(point)
			} else {
				const bearing = turf.bearing(firstPoint, secondPoint)
				const distance1 = turf.distance(firstPoint, secondPoint)
				const distance2 = turf.distance(firstPoint, point)

				const oppositePoint1 = turf.destination(firstPoint, distance1, bearing + 180)
				const oppositePoint2 = turf.destination(firstPoint, distance2, bearing + 90)
				const oppositePoint3 = turf.destination(firstPoint, distance2, bearing - 90)

				const polygon = turf.polygon([
					[
						oppositePoint1.geometry.coordinates,
						oppositePoint2.geometry.coordinates,
						secondPoint.geometry.coordinates,
						oppositePoint3.geometry.coordinates,
						oppositePoint1.geometry.coordinates,
					],
				])

				onComplete?.(polygon.geometry)
				reset()
			}
		},
		[firstPoint, onComplete, reset, secondPoint],
	)

	const handleMouseMove = useCallback<MapEventHandler<"mousemove">>(
		(e) => {
			const { lngLat } = e

			if (!firstPoint) return

			const point = turf.point(lngLat.toArray())
			setTempPoint(point)
		},
		[firstPoint],
	)

	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("mousemove", handleMouseMove)

	const geojson = useMemo<FeatureCollection>(() => {
		const features: Feature[] = []

		if (firstPoint && !secondPoint) {
			features.push(firstPoint)
		}

		if (!secondPoint && tempPoint) {
			features.push(tempPoint)
		}

		if (firstPoint && !secondPoint && tempPoint) {
			const bearing = turf.bearing(firstPoint, tempPoint)
			const distance1 = turf.distance(firstPoint, tempPoint)
			const oppositePoint = turf.destination(firstPoint, distance1, bearing + 180)
			features.push(oppositePoint)
			features.push(turf.lineString([oppositePoint.geometry.coordinates, tempPoint.geometry.coordinates]))
		}

		if (firstPoint && secondPoint && tempPoint) {
			const bearing = turf.bearing(firstPoint, secondPoint)
			const distance1 = turf.distance(firstPoint, secondPoint)
			const distance2 = turf.distance(firstPoint, tempPoint)

			const oppositePoint1 = turf.destination(firstPoint, distance1, bearing + 180)
			const oppositePoint2 = turf.destination(firstPoint, distance2, bearing + 90)
			const oppositePoint3 = turf.destination(firstPoint, distance2, bearing - 90)

			const polygon = turf.polygon([
				[
					oppositePoint1.geometry.coordinates,
					oppositePoint2.geometry.coordinates,
					secondPoint.geometry.coordinates,
					oppositePoint3.geometry.coordinates,
					oppositePoint1.geometry.coordinates,
				],
			])

			features.push(oppositePoint2)
			features.push(oppositePoint3)
			// @ts-expect-error REASON: sector is a singular Polygon feature
			features.push(turf.polygonToLine(polygon))
			features.push(polygon)
		}

		return turf.featureCollection(features)
	}, [firstPoint, secondPoint, tempPoint])

	return (
		<Source id={DIAMOND_DRAWER_SOURCE_ID} type="geojson" data={geojson}>
			<Layer
				id={DiamondDrawerLayedId.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={DiamondDrawerLayedId.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={DiamondDrawerLayedId.CIRCLE}
			/>
			<Layer
				id={DiamondDrawerLayedId.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={DiamondDrawerLayedId.LINE}
			/>
		</Source>
	)
})
DiamondDrawer.displayName = "RectangleDrawer"
